import {
  actionColumn,
  ButtonMenu,
  column,
  Css,
  emptyCell,
  fieldColumn,
  getTableStyles,
  GridColumn,
  GridTable,
  Icon,
  IconKey,
  MenuItem,
  Palette,
  RowStyles,
  simpleHeader,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { FieldState, ObjectState } from "@homebound/form-state";
import { camelCase } from "change-case";
import { ReactNode, useMemo } from "react";
import { BoundAiNumberField } from "src/components/formFields/ai";
import { pendingSources } from "src/components/formFields/ai/AiProposalValue";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  type AiFieldBinding,
  reconcilePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiFieldProposalStatus, Maybe } from "src/generated/graphqlTypes";
import { openNewTab } from "src/utils/window";

export type ProgramDataFeature = {
  label: string;
  base: FieldState<Maybe<number>>;
  min: Maybe<number>;
  max: Maybe<number>;
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
  /** This row's field + parser, so its menu can Accept/Ignore without touching the rest of the tab. */
  binding: AiFieldBinding;
};

type ProgramDataCardProps = {
  title: string;
  icon: IconKey;
  /** Enums have no min/max, so they render above the table rather than as a feature row. */
  enumField?: ReactNode;
  features?: ProgramDataFeature[];
  disabled: ReactNode | undefined;
};

export function ProgramDataCard(props: ProgramDataCardProps) {
  const { title, icon, enumField, features, disabled } = props;
  const columns = useMemo(() => createColumns(disabled), [disabled]);
  const rows = useMemo(
    () => [simpleHeader, ...(features ?? []).map((f) => ({ id: f.label, kind: "data" as const, data: f }))],
    [features],
  );

  return (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.df.aic.gap1.$}>
        <Icon icon={icon} />
        <h3 css={Css.mdSb.$}>{title}</h3>
      </div>
      {enumField && <div css={Css.py1.$}>{enumField}</div>}
      {features && (
        <GridTable
          id={`${camelCase(title)}Table`}
          columns={columns}
          rows={rows}
          style={tableStyle}
          rowStyles={rowStyles}
        />
      )}
    </div>
  );
}

type Row = SimpleHeaderAndData<ProgramDataFeature>;

const tableStyle = {
  ...getTableStyles({ bordered: true, allWhite: true, inlineEditing: true, vAlign: "top" }),
  rootCss: Css.mw0.$,
};

// GridTable rows are observers, so reading proposal state here re-renders only this row.
// Hover uses `--beam-row-hover-bg` (Beam paints `.beam-row-hover:hover > *` from that var).
const proposedHighlightCss = Css.bgPurple50.style({ "--beam-row-hover-bg": Palette.Purple100 }).$;
const rowStyles: RowStyles<Row> = {
  data: { cellCss: ({ data }) => (hasProposedHighlight(data) ? proposedHighlightCss : {}) },
};

function hasProposedHighlight(feature: ProgramDataFeature): boolean {
  return feature.aiProposal?.status.value === AiFieldProposalStatus.Pending;
}

function createColumns(disabled: ReactNode | undefined): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Feature", data: ({ label }) => label }),
    fieldColumn<Row>({
      header: "Base",
      data: ({ label, base, aiProposal }) => (
        <BoundAiNumberField
          label={label}
          labelStyle="hidden"
          field={base}
          aiProposal={aiProposal}
          hideAiSources
          disabled={disabled}
          xss={Css.tal.jcfs.$}
        />
      ),
      w: "100px",
    }),
    column<Row>({ header: "Min", data: ({ min }) => min ?? "—", w: "80px" }),
    column<Row>({ header: "Max", data: ({ max }) => max ?? "—", w: "80px" }),
    actionColumn<Row>({
      header: emptyCell,
      data: (feature) =>
        hasProposedHighlight(feature) ? <FeatureMenu feature={feature} disabled={disabled} /> : emptyCell,
      w: "40px",
    }),
  ];
}

type FeatureMenuProps = { feature: ProgramDataFeature; disabled: ReactNode | undefined };

/** Review actions for one proposed feature, matching the Locations and Options row menus. */
function FeatureMenu(props: FeatureMenuProps) {
  const { feature, disabled } = props;
  const resolve = (decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) => () =>
    reconcilePendingAiProposals([feature.binding], decision);
  const sources = pendingSources(feature.aiProposal);
  const items: MenuItem[] = [
    { label: "Accept Changes", ai: true, disabled, onClick: resolve(AiFieldProposalStatus.Accepted) },
    { label: "Ignore Change", ai: true, disabled, onClick: resolve(AiFieldProposalStatus.Rejected) },
    // TODO: PLAN-1846 — route this to the new source PDF viewer, which can show every cited page.
    // Until then we open the document at the first one.
    ...(sources
      ? [
          {
            label: "View Source",
            ai: true,
            onClick: () => openNewTab(`${sources.sourceUrl}#page=${sources.pageNumbers[0]}`),
          },
        ]
      : []),
  ];
  return <ButtonMenu trigger={{ icon: "verticalDots" }} placement="right" items={items} />;
}
